import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import TextInput from '../textInput'; // Assuming TextInput handles label prop correctly
import { saveAs } from 'file-saver';
import SEO from "../components/SEO"; // Adjust the import path as necessary

const DebtServiceRatioCalculator = () => {
    const [monthlyIncome, setMonthlyIncome] = useState(''); // Should be Net Income
    const [debts, setDebts] = useState([]);
    const [newDebt, setNewDebt] = useState({
        name: '',
        payment: '',
        type: 'housing',
        label: ''
    });
    const [calculationResult, setCalculationResult] = useState(null);
    const [analysis, setAnalysis] = useState('');

    const debtTypes = [
        { value: 'housing', label: 'Housing Loan' },
        { value: 'car', label: 'Car Loan' },
        { value: 'personal', label: 'Personal Loan' },
        { value: 'credit', label: 'Credit Card' },
        { value: 'ptptn', label: 'Education Loan' },
        { value: 'other', label: 'Other Debt' }
    ];

    // DSR Calculation as per Malaysian Banking Standards
    const calculateDSR = () => {
        // Use Net Income (Income after EPF, SOCSO, PCB/Tax)
        const netIncome = parseFloat(monthlyIncome);
        if (isNaN(netIncome) || netIncome <= 0) {
            alert('Please enter a valid monthly net income.');
            return;
        }

        if (debts.length === 0) {
             // Allow calculation even with zero debts (DSR would be 0%)
             // Set result directly without alert if needed, or keep alert if desired.
            // For now, keeping the alert as it might imply user forgot to add debts.
             alert('Please add at least one debt commitment if you have any. If none, your DSR is 0%.');
             // Optionally calculate and show 0% DSR:
             /*
             setCalculationResult({
                 dsr: 0, housingDSR: 0, carDSR: 0, otherDSR: 0,
                 totalDebtPayments: 0, income: netIncome
             });
             setAnalysis('Your debt service ratio is 0% (Excellent). You have no listed debt commitments.');
             return;
             */
             // Sticking with alert for now based on original logic.
             return;
        }

        // Sum of *all* monthly commitments (including credit card minimums, PTPTN, etc.)
        const totalDebtPayments = debts.reduce((sum, debt) => {
            // Ensure payment is treated as a number
            const payment = parseFloat(debt.payment || 0);
            return sum + (isNaN(payment) ? 0 : payment);
        }, 0);

        // DSR Formula: (Total Monthly Commitments / Monthly Net Income) * 100
        const dsr = netIncome > 0 ? (totalDebtPayments / netIncome) * 100 : 0; // Avoid division by zero

        // --- Type-specific DSR calculation (remains the same conceptually) ---
         const housingDSR = debts.filter(debt => debt.type === 'housing')
            .reduce((sum, debt) => sum + parseFloat(debt.payment || 0), 0) / netIncome * 100;

        const carDSR = debts.filter(debt => debt.type === 'car')
            .reduce((sum, debt) => sum + parseFloat(debt.payment || 0), 0) / netIncome * 100;

        // Ensure otherDSR is not negative due to potential floating point inaccuracies
        const otherDSR = Math.max(0, dsr - housingDSR - carDSR);
        // --- End Type-specific DSR ---


        const result = {
            dsr: dsr,
            housingDSR: housingDSR,
            carDSR: carDSR,
            otherDSR: otherDSR,
            totalDebtPayments: totalDebtPayments,
            income: netIncome // Keep track of the net income used
        };

        setCalculationResult(result);

        // --- Analysis based on Malaysian financial standards (remains the same) ---
        let dsrAnalysis = '';
        if (dsr <= 30) {
            dsrAnalysis = 'Your debt service ratio is healthy (below 30%). You have good financial flexibility and are likely eligible for additional financing if needed.';
        } else if (dsr <= 60) {
            dsrAnalysis = 'Your debt service ratio is moderate (30-60%). Most Malaysian banks consider a DSR up to 60%-70% acceptable depending on income level, but you should be cautious about taking on more debt.';
        } else if (dsr <= 70) {
             // Adjusted threshold slightly based on search results suggesting 70-75% for higher income
             dsrAnalysis = 'Your debt service ratio is high (60-70%). You may still qualify for loans from some banks (especially if income is higher) but potentially at less favorable terms. Consider reducing your debt.';
        } else {
            dsrAnalysis = 'Your debt service ratio is very high (above 70%). Most Malaysian financial institutions would consider this risky. Focus on debt reduction before taking on new commitments.';
        }

        // Keep housing check if relevant
        // if (housingDSR > 30) {
        //    dsrAnalysis += ' Your housing loan payments might be a significant portion of your income.';
        // }
        setAnalysis(dsrAnalysis);
        // --- End Analysis ---
    };


    // --- Other handlers remain the same ---
    const handleAddDebt = () => {
        if (!newDebt.name || !newDebt.payment) {
            alert('Please enter both name and payment amount for the debt.');
            return;
        }
        const payment = parseFloat(newDebt.payment);
        if (isNaN(payment) || payment < 0) { // Allow 0 payment if needed, but typically > 0
            alert('Please enter a valid, non-negative payment amount.');
            return;
        }
        setDebts([...debts, { ...newDebt, payment: payment }]);
        setNewDebt({ name: '', payment: '', type: 'housing', label: '' });
    };

    const handleRemoveDebt = (index) => {
        const updatedDebts = [...debts];
        updatedDebts.splice(index, 1);
        setDebts(updatedDebts);
         // Optional: Recalculate DSR immediately after removing a debt if result is already shown
         if (calculationResult) {
             // Create a temporary new debt list to pass to a modified calculate function
             // Or simply call calculateDSR() again which will use the updated state 'debts'
             // Note: Calling calculateDSR() might cause issues if monthlyIncome hasn't been entered yet.
             // Best might be to clear the result or let user recalculate manually.
             // For simplicity, we let the user press "Calculate DSR" again.
             setCalculationResult(null); // Clear previous result
             setAnalysis('');
         }
    };

     const handleExportCsv = () => {
        if (!calculationResult) return;
        let csvContent = "Debt Name,Monthly Payment,Type\n";
        debts.forEach(debt => {
            csvContent += `"${debt.name.replace(/"/g, '""')}",${debt.payment},${debt.type}\n`;
        });
        csvContent += "\nDSR Analysis\n";
        csvContent += `Monthly Net Income,${calculationResult.income}\n`;
        csvContent += `Total Debt Payments,${calculationResult.totalDebtPayments}\n`;
        csvContent += `Overall DSR,${calculationResult.dsr.toFixed(2)}%\n`;
        csvContent += `Housing DSR,${calculationResult.housingDSR.toFixed(2)}%\n`;
        csvContent += `Car DSR,${calculationResult.carDSR.toFixed(2)}%\n`;
        csvContent += `Other DSR,${calculationResult.otherDSR.toFixed(2)}%\n`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, 'dsr_analysis.csv');
    };

    const getDebtTypeLabel = (type) => {
        const found = debtTypes.find(t => t.value === type);
        return found ? found.label : type;
    };

    const dsrChartData = calculationResult ? [
        { name: 'Housing', value: calculationResult.housingDSR },
        { name: 'Car', value: calculationResult.carDSR },
        { name: 'Other Debts', value: calculationResult.otherDSR },
        { name: 'Remaining Income %', value: Math.max(0, 100 - calculationResult.dsr) }
    ].filter(item => item.value > 0.01) : [];

    const chartColors = ['#005E58', '#00A79D', '#45CFCA', '#D3F0EE'];

    // --- JSX Structure (UI/UX remains the same, only text/labels updated) ---
    return (
        <>
            {/* SEO remains the same */}
             <SEO
                title="Debt Service Ratio (DSR) Calculator Malaysia | Jimatly"
                description="Calculate your Debt Service Ratio (DSR) based on Malaysian banking standards using your net income and monthly commitments. Check your loan affordability."
                canonical="https://www.jimatly.com/dsr-calculator"
                ogImage="https://www.jimatly.com/dsr-og-image.jpg"
            />
            <div className="dsr-calculator-page">
                 {/* Styles remain the same */}
                 <style>{`
                    /* --- Styles (Unchanged from previous version - truncated for brevity) --- */
                    .dsr-calculator-page { font-family: 'Inter', -apple-system, sans-serif; max-width: 1000px; margin: 0 auto; padding: 1rem; color: #333; }
                    h1, h2, h3 { color: #00A79D; font-weight: 500; }
                    h1 { text-align: center; margin-bottom: 1.5rem; font-size: 1.8rem; }
                    h2 { font-size: 1.5rem; margin-top: 1.5rem; margin-bottom: 1rem; border-bottom: 2px solid #eee; padding-bottom: 0.5rem; }
                    h3 { font-size: 1.2rem; margin-top: 1rem; margin-bottom: 0.75rem; }
                    .info-panel { background: #f5f9fa; border-left: 4px solid #00A79D; padding: 1rem; margin-bottom: 1.5rem; border-radius: 4px; font-size: 0.9rem; }
                    .input-section, .debt-form, .debts-list, .result-section { background: white; border-radius: 8px; padding: 1.5rem; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05); margin-bottom: 1.5rem; }
                    input, select { display: block; width: 100%; padding: 0.75rem; border: 1px solid #ddd; border-radius: 4px; font-size: 0.9rem; transition: border-color 0.2s; box-sizing: border-box; margin-bottom: 0.75rem; }
                    input:focus, select:focus { outline: none; border-color: #00A79D; box-shadow: 0 0 0 2px rgba(0, 167, 157, 0.2); }
                    button { background: #00A79D; color: white; border: none; border-radius: 4px; padding: 0.75rem 1.5rem; font-size: 0.9rem; cursor: pointer; transition: background-color 0.2s; display: inline-block; }
                    button:hover { background: #008C84; }
                    .debt-form .calculation-notes { font-size: 0.85rem; color: #555; margin-top: -0.5rem; margin-bottom: 1rem; background-color: #f9f9f9; padding: 0.75rem; border-radius: 4px; border: 1px solid #eee; }
                    .debt-form .calculation-notes ul { margin: 0.5rem 0 0 1rem; padding-left: 0.5rem; }
                    .debt-form .calculation-notes li { margin-bottom: 0.25rem; }
                    .debt-input-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 0.75rem; align-items: end; }
                    .debt-input-row button { margin-bottom: 0; align-self: end; padding: 0.75rem; }
                    .debts-list { margin-top: 1.5rem; }
                    .table-container { overflow-x: auto; }
                    table { width: 100%; border-collapse: collapse; margin-top: 1rem; }
                    th, td { padding: 0.75rem; text-align: left; border-bottom: 1px solid #eee; white-space: nowrap; font-size: 0.9rem; } /* Added font-size */
                    th { font-weight: 500; color: #666; }
                    td button { padding: 0.3rem 0.6rem; font-size: 0.8rem; background-color: #f44336; line-height: 1; }
                    .action-buttons { display: flex; justify-content: center; margin: 1.5rem 0; }
                    .primary-button { background: #00A79D; padding: 0.75rem 2rem; font-size: 1rem; font-weight: 500; }
                    .chart-container-wrapper { width: 100%; height: 300px; margin: 1rem auto; max-width: 400px; }
                    .results-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; margin-top: 1.5rem; }
                    .dsr-stats ul { list-style: none; padding: 0; }
                    .dsr-stats li { margin-bottom: 0.5rem; padding: 0.75rem; border-radius: 4px; background: #f9f9f9; display: flex; justify-content: space-between; flex-wrap: wrap; font-size: 0.9rem; } /* Added font-size */
                    .dsr-stats li span { margin-right: 0.5rem; }
                    .analysis-box { background: #f5f5f5; padding: 1rem; border-radius: 6px; border-left: 4px solid #00A79D; margin: 1.5rem 0; font-size: 0.9rem; }
                    .dsr-meter { height: 1.5rem; background: #eee; border-radius: 1rem; overflow: hidden; margin: 1rem 0 0.5rem 0; position: relative; }
                    .dsr-meter-fill { height: 100%; background: linear-gradient(to right, #45CFCA, #00A79D, #005E58); transition: width 0.5s ease-out; }
                    .dsr-meter-labels { display: flex; justify-content: space-between; font-size: 0.75rem; color: #666; padding: 0 0.25rem; }
                    .export-options { margin-top: 1.5rem; text-align: center; }
                    .export-options h3 { margin-bottom: 0.75rem; }
                    .debt-indicator { display: inline-block; padding: 0.25rem 0.5rem; border-radius: 4px; font-size: 0.75rem; font-weight: 500; color: white; margin-top: 0.25rem; }
                    .debt-indicator.housing { background-color: #005E58; }
                    .debt-indicator.car { background-color: #00A79D; }
                    .debt-indicator.personal, .debt-indicator.credit, .debt-indicator.ptptn, .debt-indicator.other { background-color: #45CFCA; }
                    .malaysian-context { font-size: 0.9rem; } /* Added font-size */
                    .malaysian-context ul { list-style: disc; padding-left: 1.5rem; margin-top: 0.5rem; }
                    .malaysian-context li { margin-bottom: 0.5rem; }
                    .malaysian-context small { display: block; margin-top: 1rem; color: #666; }

                    @media (min-width: 768px) {
                        .dsr-calculator-page { padding: 2rem; }
                        .debt-input-row { grid-template-columns: 2fr 1.5fr 1fr 1.5fr auto; }
                        input, select { margin-bottom: 0; }
                        .results-grid { grid-template-columns: 1fr 1fr; }
                         .chart-container-wrapper { height: 350px; }
                    }
                    .recharts-legend-item-text { color: #333 !important; }
                    .recharts-tooltip-label { color: #333 !important; }
                    .recharts-pie-label-text { fill: #333 !important; font-size: 0.8rem; }
                 `}</style>

                <h1><strong>Debt Service Ratio (DSR) Calculator</strong></h1>

                <div className="info-panel">
                    <p>Calculate your Debt Service Ratio (DSR) based on Malaysian banking standards. This helps estimate your loan affordability.</p>
                    <p><strong>Note:</strong> This calculator only provides an estimate. Final loan approval depends on the bank's specific criteria, your credit score (CCRIS/CTOS), and other factors.</p>
                </div>

                {/* Section for Income Input */}
                <div className="input-section">
                     <h2>Your Income</h2>
                     <TextInput
                        // Updated Label to specify Net Income
                        label="Monthly Net Income (RM)"
                        // Updated placeholder
                        placeholder="Income after EPF, SOCSO, Tax"
                        value={monthlyIncome}
                        onChange={(e) => setMonthlyIncome(e.target.value)}
                        required
                        type="number"
                        min="0" // Ensure non-negative input
                    />
                </div>

                {/* Section for Adding Debts */}
                <div className="debt-form">
                    <h2>Add Your Monthly Debt Commitments</h2>
                     {/* Added clarification note */}
                    <div className="calculation-notes">
                        <strong>Important Notes for Accurate Calculation:</strong>
                        <ul>
                            <li>Enter your <strong>Net Income</strong> (after mandatory deductions like EPF, SOCSO, PCB/Tax).</li>
                            <li>Include <strong>all</strong> monthly debt payments (housing, car, personal loans, PTPTN, etc.).</li>
                            <li>For <strong>Credit Cards:</strong> Enter the <strong>minimum monthly payment</strong> (usually 5% of the outstanding balance, or min RM 50, whichever is higher). Check your statement.</li>
                            <li>For ASB Financing/Other variable payments, enter the typical monthly amount.</li>
                        </ul>
                    </div>
                    {/* Container for debt input fields */}
                    <div className="debt-input-container">
                        <div className="debt-input-row">
                           <TextInput
                                label="Debt Name"
                                type="text"
                                placeholder="e.g., Maybank Home Loan"
                                value={newDebt.name}
                                onChange={(e) => setNewDebt({...newDebt, name: e.target.value})}
                            />

                           <TextInput
                                label="Monthly Payment (RM)"
                                type="number"
                                min="0" // Ensure non-negative
                                placeholder="e.g., 1500"
                                value={newDebt.payment}
                                onChange={(e) => setNewDebt({...newDebt, payment: e.target.value})}
                            />
                             <div>
                                <label style={{display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: '500'}}>Debt Type</label>
                                <select
                                     value={newDebt.type}
                                     onChange={(e) => setNewDebt({...newDebt, type: e.target.value})}
                                >
                                    {debtTypes.map((type, idx) => (
                                        <option key={idx} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                            </div>
                            <button onClick={handleAddDebt}>Add</button>
                        </div>
                    </div>
                </div>

                 {/* Section for Displaying Added Debts (No functional changes) */}
                {debts.length > 0 && (
                    <div className="debts-list">
                        <h2>Your Debts</h2>
                        <div className="table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Debt Name</th>
                                        <th>Monthly Payment</th>
                                        <th>Type</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {debts.map((debt, index) => (
                                        <tr key={index}>
                                            <td>{debt.name}</td>
                                            <td>RM {parseFloat(debt.payment || 0).toFixed(2)}</td>
                                            <td>
                                                <span className={`debt-indicator ${debt.type}`}>
                                                    {getDebtTypeLabel(debt.type)}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() => handleRemoveDebt(index)}
                                                    title="Remove Debt"
                                                >
                                                    ✕
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                 {/* Action Button to Calculate (No functional changes) */}
                <div className="action-buttons">
                    <button onClick={calculateDSR} className="primary-button">Calculate DSR</button>
                </div>

                 {/* Section for Displaying Results (UI remains same, uses calculated DSR) */}
                {calculationResult && (
                    <div className="result-section">
                        <h2>Your DSR Analysis</h2>

                        {/* DSR Meter */}
                        <div className="dsr-meter-container">
                            <h3><strong>Overall DSR:</strong> {calculationResult.dsr.toFixed(2)}%</h3>
                            <div className="dsr-meter">
                                <div
                                    className="dsr-meter-fill"
                                    style={{width: `${Math.min(calculationResult.dsr, 100)}%`}}
                                ></div>
                            </div>
                            <div className="dsr-meter-labels">
                                <span>0%</span>
                                <span>30% (Healthy)</span>
                                <span>60% (Moderate)</span>
                                <span>70% (High)</span>
                                <span>100%</span>
                            </div>
                        </div>

                        {/* Results Grid */}
                        <div className="results-grid">
                             {/* Chart */}
                            <div className="chart-container-wrapper">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={dsrChartData}
                                            cx="50%"
                                            cy="50%"
                                            outerRadius="80%"
                                            fill="#8884d8"
                                            dataKey="value"
                                            nameKey="name"
                                             labelLine={false}
                                             label={({ name, percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                                        >
                                            {dsrChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value, name, props) => [`${value.toFixed(2)}% of Net Income`, name]} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            {/* DSR Stats */}
                            <div className="dsr-stats-container">
                                <div className="dsr-stats">
                                    <h3>DSR Breakdown</h3>
                                    <ul>
                                        <li>
                                            <span>Monthly Net Income:</span>
                                            <strong>RM {calculationResult.income.toFixed(2)}</strong>
                                        </li>
                                        <li>
                                            <span>Total Debt Payments:</span>
                                            <strong>RM {calculationResult.totalDebtPayments.toFixed(2)}</strong>
                                        </li>
                                         {/* Displaying type-specific DSRs as percentages */}
                                         {calculationResult.housingDSR > 0 && (
                                            <li style={{background: chartColors[0], color: 'white'}}>
                                                <span>Housing DSR %:</span>
                                                <strong>{calculationResult.housingDSR.toFixed(2)}%</strong>
                                            </li>
                                         )}
                                         {calculationResult.carDSR > 0 && (
                                            <li style={{background: chartColors[1], color: 'white'}}>
                                                <span>Car DSR %:</span>
                                                <strong>{calculationResult.carDSR.toFixed(2)}%</strong>
                                            </li>
                                         )}
                                          {calculationResult.otherDSR > 0 && (
                                            <li style={{background: chartColors[2], color: '#333'}}>
                                                <span>Other Debts DSR %:</span>
                                                <strong>{calculationResult.otherDSR.toFixed(2)}%</strong>
                                            </li>
                                         )}
                                            {calculationResult.overallDSR > 0 && (
                                            <li style={{background: chartColors[2], color: '#333'}}>
                                                <span>Overall DSR %:</span>
                                                <strong>{calculationResult.overallDSR.toFixed(2)}%</strong>
                                            </li>
                                         )}
                                          {calculationResult.dsr <= 100 && ( // Only show remaining income if DSR is not over 100%
                                             <li style={{background: chartColors[3], color: '#333'}}>
                                                <span>Remaining Income %:</span>
                                                <strong>{Math.max(0, 100 - calculationResult.dsr).toFixed(2)}%</strong>
                                            </li>
                                          )}
                                    </ul>
                                </div>
                           </div>
                        </div>

                        {/* Text Analysis */}
                        <div className="analysis-container">
                           <div className="analysis-box">
                                <h3>Interpretation</h3>
                                <p>{analysis}</p>
                            </div>
                        </div>

                         {/* Malaysian Context */}
                        <div className="malaysian-context-container">
                           <div className="malaysian-context">
                                <h3>Malaysian Loan Eligibility Guide</h3>
                                <p>Based on your current DSR of <strong>{calculationResult.dsr.toFixed(2)}%</strong>:</p>
                                <ul>
                                    {calculationResult.dsr <= 30 && (
                                        <li>✅ Excellent loan eligibility.</li>
                                    )}
                                    {calculationResult.dsr > 30 && calculationResult.dsr <= 60 && (
                                        <li>👍 Good loan eligibility (Standard range for many banks).</li>
                                    )}
                                    {calculationResult.dsr > 60 && calculationResult.dsr <= 70 && (
                                        <li>⚠️ Acceptable for some banks/higher income earners, but may face tighter conditions.</li>
                                    )}
                                    {calculationResult.dsr > 70 && (
                                        <li>❌ High risk; likely difficult to qualify for new loans. Focus on reducing existing debt.</li>
                                    )}
                                </ul>
                                <small>Note: Banks assess affordability using DSR alongside credit history (CCRIS/CTOS), income stability, NDI (Net Disposable Income), and their internal risk appetite. Max DSR limits vary between banks.</small>
                            </div>
                        </div>

                        {/* Export Options */}
                        <div className="export-options-container">
                             <div className="export-options">
                                <h3>Export Your Analysis</h3>
                                <button onClick={handleExportCsv}>Export to CSV</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default DebtServiceRatioCalculator;